import { type CSSObject, keyframes } from "@emotion/react";

/**
 * A CSS-only take on lucide-animated's `trending-up` / `trending-down` motion
 * (https://lucide-animated.com), which ships as a Motion-powered React
 * component. Same three beats, no dependency — but played on mount rather than
 * on hover, so a trend glyph draws itself alongside `useCountUp`'s tally
 * instead of waiting for a pointer that may never arrive:
 *
 *  1. the whole glyph nudges the way its arrow points and settles back (500ms),
 *  2. the trend line draws tail → arrow head, the direction of travel (400ms),
 *  3. the arrow head draws once the line has nearly arrived (300ms, 280ms in).
 *
 * Motion animates `pathLength`; the CSS equivalent is a dash the length of the
 * path walked to zero offset. Both glyphs are drawn from the same two `<path>`
 * elements, so the lengths in `TREND_PATHS` are fixed geometry, not guesses:
 *   head "M16 7h6v6"             → 6 + 6                 = 12 user units
 *   line "m22 7-8.5 8.5-5-5L2 17" → 12.02 + 7.07 + 9.19  ≈ 28.3 user units
 * Each dash is rounded up so the glyph is fully hidden at 0%.
 *
 * The line's offset runs *negative* → 0 on purpose. Both line paths are
 * authored starting at the arrow head and running back to the tail, so a
 * positive offset would draw the stroke backwards, away from the arrow. A
 * negative one grows the dash in from the far end: up-and-right for
 * `trending-up`, down-and-right for `trending-down`, arriving under the head
 * just as the head itself appears.
 */

const nudgeUp = keyframes`
  0%   { transform: translate(0, 0); }
  50%  { transform: translate(2px, -2px); }
  100% { transform: translate(0, 0); }
`;

const nudgeDown = keyframes`
  0%   { transform: translate(0, 0); }
  50%  { transform: translate(2px, 2px); }
  100% { transform: translate(0, 0); }
`;

/**
 * The two paths in the order they are rendered: index + 1 is the `nth-of-type`
 * each animation hangs on, and that order is NOT the one lucide authors. These
 * rows render an animated twin (`registry.ts`) and `@animateicons/react` emits
 * the line FIRST, the reverse of lucide's own `trending-up` — which is how the
 * dashes came to be swapped when the twin landed, a 13-unit dash tiling a
 * 28-unit line while the head drew with one twice its length. Both still
 * animated, so nothing complained.
 *
 * Hence one entry per path, carrying its own dash and the `d` it was measured
 * from: the pairing is no longer a convention two selectors have to keep, and
 * `Icon.animated.test.tsx` holds `startsWith` against the rendered glyph, so a
 * reordered twin fails there rather than shipping as a glyph that looks wrong.
 */
export const TREND_PATHS = [
  {
    fadeInAt: "25%",
    offset: -30,
    startsWith: "m22 ",
    timing: "400ms ease-out backwards"
  },
  {
    fadeInAt: "33%",
    offset: 13,
    startsWith: "M16 ",
    // `backwards` holds the hidden 0% frame through the delay, so the head does
    // not sit there fully drawn waiting for its turn.
    timing: "300ms ease-out 280ms backwards"
  }
] as const;

// The dash lives in the keyframes rather than in the rule so it exists only
// while the animation runs — a settled glyph is an ordinary unbroken stroke.
const draw = ({
  fadeInAt,
  offset
}: (typeof TREND_PATHS)[number]): ReturnType<typeof keyframes> => keyframes`
  0%          { opacity: 0; stroke-dasharray: ${Math.abs(offset)}; stroke-dashoffset: ${offset}; }
  ${fadeInAt} { opacity: 1; }
  100%        { opacity: 1; stroke-dasharray: ${Math.abs(offset)}; stroke-dashoffset: 0; }
`;

export const trendPathSelector = (nth: number): string =>
  `[data-icon^="trending-"]:not(:hover) path:nth-of-type(${nth})`;

/**
 * Entrance styles for a container holding a `trending-up` / `trending-down`
 * `Icon`. Spread into the sx of whichever element wraps the glyph; the
 * animation plays once, when that glyph mounts.
 *
 * Selectors hang off `data-icon`, which `Icon` already renders, so nothing has
 * to be threaded through the atom. The dash only exists while the animation
 * runs: at rest the paths carry no dasharray at all, so a settled icon is
 * untouched and there is nothing to clean up if the animation is skipped.
 *
 * Every selector stands down under `:not(:hover)`. These rows also carry a
 * hover twin, and the two would otherwise drive the same glyph at once.
 *
 * To replay it — the equivalent of `useCountUp` re-running on a new target —
 * remount the glyph by keying it on the figure it describes. CSS animations
 * fire on mount, not on prop changes.
 */
export const trendIconEntranceSx: CSSObject = {
  "@media (prefers-reduced-motion: no-preference)": {
    '[data-icon="trending-down"]:not(:hover)': {
      animation: `${nudgeDown} 500ms ease-in-out`
    },
    '[data-icon="trending-up"]:not(:hover)': {
      animation: `${nudgeUp} 500ms ease-in-out`
    },
    ...Object.fromEntries(
      TREND_PATHS.map((path, index) => [
        trendPathSelector(index + 1),
        { animation: `${draw(path)} ${path.timing}` }
      ])
    )
  }
};
